import { Box, Button, Flex, Heading, Stack, Text, useToast } from '@homebotapp/hb-react-component-catalog'
import { defineMessages, useIntl } from 'react-intl'
import { AvatarBubbles } from '../../../../../../../shared/AvatarBubbles/AvatarBubbles'
import { useSendPrequalStep } from '../../../../../../../../hooks/homebotNetwork/verificationFlow/useSendPrequalStep'
import { Listing } from '../../../../../../../../api/gqlaxy/gql/generated/graphql'

const MSG = defineMessages({
  scheduleTourConfirmation: {
    id: 'ScheduleTour.confirmation.header',
    defaultMessage: 'Tour request sent!'
  },
  meantime: {
    id: 'ScheduleTour.confirmation.meantime',
    defaultMessage: 'In the meantime, would you like to get pre-qualified?'
  },
  backToListing: {
    id: 'ScheduleTour.confirmation.backToListing',
    defaultMessage: 'Back to listing'
  },
  add: {
    id: 'ScheduleTour.confirmation.add',
    defaultMessage: 'Add'
  },
  prequalExplanation: {
    id: 'ScheduleTour.confirmation.prequalExplanation',
    defaultMessage:
      'Pre-qualifying gives you a clear budget with no obligation to proceed, so you can have a fully informed conversation.'
  },
  requestPrequal: {
    id: 'ScheduleTour.confirmation.requestPrequal',
    defaultMessage: 'Request pre-qual'
  },
  willFollowUp: {
    id: 'ScheduleTour.confirmation.willFollowUp',
    defaultMessage:
      '{customerFirstName} will follow up to finalize a date and time. If you have any questions, please contact them.'
  },
  prequalError: {
    id: 'SendPrequalView.prequalError',
    defaultMessage: 'Failed to send prequal request'
  },
  prequalSuccess: {
    id: 'SendPrequalView.prequalSuccess',
    defaultMessage: 'Pre-qualification request sent!'
  }
})

/**
 * SendPrequalView specific UI for the schedule tour verification flow
 *
 */
export const SendPrequalView = ({ listing }: { listing?: Listing }) => {
  const intl = useIntl()
  const toast = useToast()
  const { onCancel, onSubmit, isLoading, customerFirstName, customerPhotoUri, corporatePhotoUri, partnerName } =
    useSendPrequalStep(listing)

  const handleSubmit = async () => {
    try {
      await onSubmit()

      toast({
        title: intl.formatMessage(MSG.prequalSuccess),
        status: 'success'
      })
    } catch (error) {
      toast({
        title: intl.formatMessage(MSG.prequalError),
        status: 'error'
      })
    }
  }

  return (
    <>
      <Box textAlign='center' data-qa='schedule-tour-send-prequal-view'>
        <Box p='6' borderBottomStyle='solid' borderBottomColor='neutral.300' borderBottomWidth='1px'>
          <Text fontWeight='bold'>✅ {intl.formatMessage(MSG.scheduleTourConfirmation)}</Text>
        </Box>
        <Text size='sm' py='6'>
          {intl.formatMessage(MSG.willFollowUp, {
            customerFirstName: partnerName
          })}
        </Text>
        <Flex direction={'row'} justifyContent={'center'} alignItems={'center'}>
          {(customerPhotoUri || corporatePhotoUri) && (
            <AvatarBubbles photoUri={customerPhotoUri} companyLogo={corporatePhotoUri} />
          )}
        </Flex>
        <Heading size='sm' fontWeight='bold' mt='4'>
          {intl.formatMessage(MSG.meantime)}
        </Heading>
        <Text size='xs'>{intl.formatMessage(MSG.prequalExplanation)}</Text>
      </Box>
      <Stack direction='row' gap={2} justifyContent='flex-end'>
        <Button
          variant='solid'
          colorScheme='secondary'
          onClick={onCancel}
          isDisabled={isLoading}
          tracking={{
            guid: 'xfmrYRRctupUR1D51Xugum',
            ui_context: 'schedule_tour_verification_flow_send_prequal_view',
            descriptor: MSG.backToListing.defaultMessage
          }}
        >
          {intl.formatMessage(MSG.backToListing)}
        </Button>
        <Button
          mr={3}
          colorScheme='primary'
          onClick={handleSubmit}
          isLoading={isLoading}
          tracking={{
            guid: 'bbEcW99iyaPDfB4CdEpN8f',
            ui_context: 'schedule_tour_verification_flow_send_prequal_view',
            descriptor: MSG.requestPrequal.defaultMessage
          }}
        >
          {intl.formatMessage(MSG.requestPrequal)}
        </Button>
      </Stack>
    </>
  )
}
